import type {TrackingServiceConfig} from '@shared/types'
import type {WebPushServiceConfig} from 'swayok-react-mdb-ui-kit/services/WebPushService'
import type {LaravelEchoConfigType} from 'swayok-react-mdb-ui-kit/services/WebSocketService'
import type {AnalyticsServiceConfig} from './types/AnalyticsService'

const crmApiUrl: string = window.config?.crmApiUrl || '/'
// URL без локали.
const rootUrl: string = window.config?.rootUrl || '/'
// URL с локалью.
const baseUrl: string = window.config?.baseUrl || '/'
const baseApiUrl: string = window.config?.baseApiUrl || ((baseUrl === '/' ? '' : baseUrl) + '/api')

const isProduction = process.env.NODE_ENV === 'production'

/**
 * Настройки приложения.
 */
export const AppConfig = {
    isProduction,
    appVersion: window.config?.appVersion ?? '1.0.0',
    baseUrl,
    rootUrl,
    baseApiUrl,

    // Тайм-аут GET запросов к API в мс.
    apiGetRequestTimeout: (isProduction ? 20 : 120) * 1000,
    // Тайм-аут POST/PUT/DELETE запросов к API в мс.
    apiPostRequestTimeout: (isProduction ? 40 : 120) * 1000,
    // Частота обновления данных пользователя в мс.
    userInfoPollingInterval: (isProduction ? 1800 : 360) * 1000,
    // Частота обновления флагов (новые сообщения, уведомления и т.п.) в мс.
    flagsPollingInterval: 60 * 1000,
    // Индикатор того, что пользователь может быть авторизован.
    // Если false, то первоначальный запрос на получение данных пользователя не выполняется.
    isAuthenticated: !!window.config?.user,
    // ID пользователя.
    userId: window.config?.user ?? null,

    /**
     * Настройка обработчика событий через websocket.
     * @see WebSocketService
     * @see WebSocketConnector
     */
    laravelEcho: {
        broadcaster: 'pusher',
        key: process.env.MIX_PUSHER_APP_KEY,
        wsHost: window.config?.wsHost && window.config.wsHost.length > 5
            ? window.config.wsHost
            : process.env.MIX_PUSHER_HOST,
        wsPort: process.env.MIX_PUSHER_PORT,
        wssPort: process.env.MIX_PUSHER_PORT,
        cluster: process.env.MIX_PUSHER_APP_CLUSTER,
        forceTLS: true,
        encrypted: true,
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        /**
         * На самом деле это ключ для защиты от автоматизации получения телефонных номеров из объявлений.
         * Ключ сохранен в обратном порядке символов и восстанавливается в onAppLoaded.ts.
         *
         * Весь процесс описан в Readme.md!
         *
         * @see onAppLoaded.ts
         * @see getApiRequestOptions.ts
         * @see getAnalyticsToken.ts
         */
        token: process.env.MIX_PHONE_PROTECTION_PUBLIC_KEY,
    } as LaravelEchoConfigType,
    /**
     * Список событий приходящих через web socket.
     * @see WebSocketEventsHandler
     */
    websocketEvent: {
        newChatMessage: 'NewChatMessage',
        chatMessagesViewed: 'ChatMessagesViewed',
        newSupportTicketComment: 'NewSupportTicketComment',
        supportTicketClosed: 'SupportTicketClosed',
    },

    /**
     * Список процессов, запускаемых по таймеру.
     * @see PollingService
     * @see DataPolling
     */
    pollingName: {
        userInfo: 'user_info',
        flags: 'flags',
    },

    // Настройки Google Maps.
    googleMaps: {
        apiKey: emptyStringToNull(window.config?.googleMaps.apiKey),
    },

    // Ключ сайта для Recaptcha.
    recaptchaSiteKey: emptyStringToNull(window.config?.recaptchaSiteKey),

    // ID клиента Яндекс Метрики.
    // yandexMetrikaClientId: window.config?.yandexMetrikaClientId ?? null,
    // ID клиента Google Analytics.
    googleAnalyticsClientId: emptyStringToNull(window.config?.googleAnalyticsClientId),
    // ID клиента Google AdSense.
    googleAdSenseId: emptyStringToNull(window.config?.googleAdSenseId),
    // ID клиента Microsoft Clarity.
    microsoftClarityId: emptyStringToNull(window.config?.microsoftClarityId),
    // ID клиента Meta Pixel.
    metaPixelId: emptyStringToNull(window.config?.metaPixelId),
    amplitudeApiKey: emptyStringToNull(window.config?.amplitudeApiKey),

    // Настройки интеграции с Facebook.
    facebook: {
        appId: emptyStringToNull(window.config?.facebookAppId),
    },

    // Настройки объявлений.
    ads: {
        maxFiles: window.config?.features?.myAds?.maxPhotos && window.config.features.myAds.maxPhotos > 4
            ? window.config.features.myAds.maxPhotos
            : 8,
        maxFileSizeKb: 2048,
        maxImageSizePx: 1280,
        imagePreviewSizePx: 125,
        jpegCompression: 0.85,
    },

    /**
     * Настройки трекера.
     * @see DeviceInfoTracker
     * @see TrackingService
     */
    trackingConfig: {
        apiUrl: crmApiUrl,
        siteId: window.config?.siteId ?? -1,
        trackingId: emptyStringToNull(window.config?.sessionKey),
    } as TrackingServiceConfig,

    /**
     * Настройки сервиса сбора аналитики.
     * @see AnalyticsService
     */
    analyticsConfig: {
        // yandexMetrikaClientId: window.config?.yandexMetrikaClientId ?? null,
        googleAnalyticsClientId: emptyStringToNull(window.config?.googleAnalyticsClientId),
        googleAnalyticsAutoTracking: window.config?.googleAnalyticsAutoTracking ?? true,
        isClarityEnabled: !!emptyStringToNull(window.config?.microsoftClarityId),
        isMetaPixelEnabled: !!emptyStringToNull(window.config?.metaPixelId),
        isAmplitudeEnabled: !!emptyStringToNull(window.config?.amplitudeApiKey),
    } as AnalyticsServiceConfig,

    /**
     * Настройки подписки на Web PUSH уведомления.
     */
    webPushConfig: {
        enabled: !!process.env.MIX_VAPID_PUBLIC_KEY,
        publicKey: process.env.MIX_VAPID_PUBLIC_KEY ?? '',
        baseApiUrl: '/v1/web-push',
        serviceWorkerScriptUrl: '/service-worker.js',
    } as Partial<WebPushServiceConfig>,

    /**
     * Настройки доступности секций и функционала.
     */
    features: window.config?.features ?? {},
}

// Преобразование пустой строки в null.
function emptyStringToNull(str: string | number | null | undefined): string | null {
    if (!str || str === 'null') {
        return null
    }
    if (typeof str !== 'number') {
        str = String(str)
    } else if (typeof str !== 'string') {
        return null
    }
    return str.trim() === '' ? null : str
}
